import React from 'react';
import { Flex, Link, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useCurrency } from '../../../../../contexts/CurrencyContext';
import { useGTM } from '../../../../../hooks';
import { useABTestVariable } from '../../../../../hooks/useABTestVariable';
import { useBuildURL } from '../../../../../hooks/useBuildURL';
import { Text } from '../../../../../ui-components';
import { CustomPreference } from '../../../../../utils/api/CustomPreference';
import { SoftLink } from '../../../../soft-link/SoftLink';
import { MenuSearchProducts } from './menu-search-products';
import type {
    BloomreachSuggestAttributeSuggestion,
    BloomreachSuggestQuerySuggestion,
} from '../../../../../bff-api/bloomreach-recommendation/model/bloomreach';
import type { Products } from '../../../../../bff-api/products/model';

interface Props {
    searchTerm: string;
    phrases: BloomreachSuggestQuerySuggestion[];
    categories: BloomreachSuggestAttributeSuggestion[];
    brands: string[];
    hardgood: Products;
    culinary: Products;
    recipe: Products;
    onClickResult: () => void;
    onClosePopover: () => void;
}

export const MenuSearchResults: React.FC<Props> = ({
    searchTerm,
    phrases,
    categories,
    brands,
    hardgood,
    culinary,
    recipe,
    onClickResult,
    onClosePopover,
}) => {
    const currency = useCurrency();
    const { buildSearchCategoryURL, buildSearchProductURL } = useBuildURL();
    const pwaSearchMode = useABTestVariable(CustomPreference.PWA_SEARCH_MODE, 'SFCC');
    const { onClickSearchSuggestion } = useGTM();

    const onClickItem = (searchSuggestion: string, triggerEvent = false): void => {
        onClickSearchSuggestion(searchTerm, searchSuggestion);
        onClickResult();

        if (triggerEvent) {
            dispatchEvent(new CustomEvent('slt-search', { detail: searchSuggestion }));
        }
        onClosePopover();
    };

    return (
        <Flex
            data-testid="header.menu.search.results"
            display="flex"
            rowGap={8}
            columnGap={6}
            direction={{ xs: 'column', md: 'row' }}
        >
            <Flex
                direction="column"
                gap={6}
                width={{ base: '100%', md: '20%' }}
                paddingX={{ base: 4, sm: 8, md: 0 }}
            >
                {phrases.length > 0 && (
                    <Flex data-testid="header.menu.search.phrases" direction="column" gap={3}>
                        {phrases.map((phrase, index) => {
                            const key = `header.menu.search.phrases.${phrase.query.replace(
                                /[^a-zA-Z0-9]/g,
                                '',
                            )}${index}`;
                            return pwaSearchMode !== 'SFCC' ? (
                                <SoftLink
                                    to={buildSearchProductURL(phrase.query)}
                                    key={key}
                                    data-testid={key}
                                    onClick={(): void => onClickItem(phrase.query, true)}
                                    className="leading-[14px] tracking-[1px]"
                                >
                                    <Text size="small">{phrase.displayText}</Text>
                                </SoftLink>
                            ) : (
                                <Link
                                    href={buildSearchProductURL(phrase.query)}
                                    key={key}
                                    data-testid={key}
                                    onClick={(): void => onClickItem(phrase.query)}
                                    className="leading-[14px] tracking-[1px]"
                                    role="link"
                                    _hover={{ textDecoration: 'none' }}
                                >
                                    <Text size="small">{phrase.displayText}</Text>
                                </Link>
                            );
                        })}
                    </Flex>
                )}

                {categories.length > 0 && (
                    <Flex
                        data-testid="header.menu.search.product.categories"
                        direction="column"
                        gap={3}
                    >
                        <Text
                            id="header.menu.search.product.categories.title"
                            textTransform="uppercase"
                            style="bold"
                        >
                            <FormattedMessage
                                id="header.menu.search.product.categories"
                                defaultMessage="Product Categories"
                            />
                        </Text>

                        {categories.map((category, index) => {
                            const key = `header.menu.search.product.categories.${category.value}${index}`;
                            return (
                                <Link
                                    key={key}
                                    data-testid={key}
                                    href={buildSearchCategoryURL(category.value)}
                                    onClick={(): void => onClickItem(category.value)}
                                    _hover={{ textDecoration: 'none' }}
                                >
                                    <Text size="small">{category.name}</Text>
                                </Link>
                            );
                        })}
                    </Flex>
                )}

                {brands.length > 0 && (
                    <Flex data-testid="header.menu.search.brands" direction="column" gap={3}>
                        <Text
                            id="header.menu.search.brands.title"
                            textTransform="uppercase"
                            style="bold"
                        >
                            <FormattedMessage
                                id="header.menu.search.brands"
                                defaultMessage="Brands"
                            />
                        </Text>

                        {brands.map((brand, index) => {
                            const key = `header.menu.search.brands.${brand.replace(
                                /[^a-zA-Z0-9]/g,
                                '',
                            )}${index}`;
                            return pwaSearchMode !== 'SFCC' ? (
                                <SoftLink
                                    key={key}
                                    data-testid={key}
                                    to={buildSearchProductURL(brand)}
                                    onClick={(): void => onClickItem(brand, true)}
                                    className="leading-[14px] tracking-[1px]"
                                >
                                    <Text size="small">{brand}</Text>
                                </SoftLink>
                            ) : (
                                <Link
                                    href={buildSearchProductURL(brand)}
                                    key={key}
                                    data-testid={key}
                                    onClick={(): void => onClickItem(brand)}
                                    textStyle="body-small"
                                    className="leading-[14px] tracking-[1px]"
                                    role="link"
                                    _hover={{ textDecoration: 'none' }}
                                >
                                    <Text size="small">{brand}</Text>
                                </Link>
                            );
                        })}
                    </Flex>
                )}
            </Flex>

            <Tabs variant="slt-enclosed">
                <TabList paddingTop={0}>
                    <Tab data-testid="header.menu.search.tab.products">
                        <FormattedMessage
                            id="header.menu.search.products"
                            defaultMessage="Products"
                        />
                    </Tab>

                    <Tab data-testid="header.menu.search.tab.classes">
                        <FormattedMessage
                            id="header.menu.search.classes"
                            defaultMessage="Classes"
                        />
                    </Tab>

                    <Tab data-testid="header.menu.search.tab.recipes">
                        <FormattedMessage
                            id="header.menu.search.recipes"
                            defaultMessage="Recipes"
                        />
                    </Tab>
                </TabList>

                <TabPanels>
                    <TabPanel data-testid="header.menu.search.tab.panel.products">
                        {hardgood.length > 0 ? (
                            <MenuSearchProducts
                                products={hardgood}
                                type="hardgood"
                                currency={currency}
                                onClick={onClickItem}
                            />
                        ) : (
                            <Flex direction="column" justify="center" width="100%">
                                <Text
                                    id="header.menu.search.tab.panel.products.empty"
                                    color="slt-red-error"
                                    className="w-full text-center"
                                >
                                    <FormattedMessage
                                        id="header.menu.search.products.empty"
                                        defaultMessage="There is no products for this search."
                                    />
                                </Text>

                                <Text
                                    id="header.menu.search.tab.panel.products.try.something.else"
                                    color="slt-red-error"
                                    className="w-full text-center"
                                >
                                    <FormattedMessage
                                        id="header.menu.search.products.try.something.else"
                                        defaultMessage="Please try something else."
                                    />
                                </Text>
                            </Flex>
                        )}
                    </TabPanel>

                    <TabPanel data-testid="header.menu.search.tab.panel.classes">
                        {culinary.length > 0 ? (
                            <MenuSearchProducts
                                products={culinary}
                                type="culinary"
                                currency={currency}
                                onClick={onClickItem}
                            />
                        ) : (
                            <Flex direction="column" justify="center" width="100%">
                                <Text
                                    id="header.menu.search.tab.panel.classes.empty"
                                    color="slt-red-error"
                                    className="w-full text-center"
                                >
                                    <FormattedMessage
                                        id="header.menu.search.classes.empty"
                                        defaultMessage="There is no class for this search."
                                    />
                                </Text>

                                <Text
                                    id="header.menu.search.tab.panel.classes.try.another.search"
                                    color="slt-red-error"
                                    className="w-full text-center"
                                >
                                    <FormattedMessage
                                        id="header.menu.search.classes.try.another.search"
                                        defaultMessage="Please try another search or location."
                                    />
                                </Text>
                            </Flex>
                        )}
                    </TabPanel>

                    <TabPanel data-testid="header.menu.search.tab.panel.recipes">
                        {recipe.length > 0 ? (
                            <MenuSearchProducts
                                products={recipe}
                                type="recipe"
                                currency={currency}
                                onClick={onClickItem}
                            />
                        ) : (
                            <Flex direction="column" justify="center" width="100%">
                                <Text
                                    id="header.menu.search.tab.panel.recipes.empty"
                                    color="slt-red-error"
                                    className="w-full text-center"
                                >
                                    <FormattedMessage
                                        id="header.menu.search.recipe.empty"
                                        defaultMessage="There is no recipe for this search."
                                    />
                                </Text>

                                <Text
                                    id="header.menu.search.tab.panel.recipes.try.something.else"
                                    color="slt-red-error"
                                    className="w-full text-center"
                                >
                                    <FormattedMessage
                                        id="header.menu.search.recipe.try.something.else"
                                        defaultMessage="Please try something else."
                                    />
                                </Text>
                            </Flex>
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Flex>
    );
};
